import { request } from "@/api/client";
import type { StrategicGoal, Project, Task, TaskMemoryRecord, TaskMemoryResponse } from "@/types/projects";

export function fetchGoals(): Promise<StrategicGoal[]> {
  return request<StrategicGoal[]>("/projects/goals");
}

export function fetchProjects(goalId: string): Promise<Project[]> {
  return request<Project[]>(`/projects/${encodeURIComponent(goalId)}/projects`);
}

export function createGoal(payload: {
  title: string;
  description?: string | null;
  priority?: "high" | "medium" | "low";
  status?: "planned" | "active" | "on_hold" | "completed";
  tags?: string[];
  due_date?: string | null;
  context?: string | null;
  deliverables?: string | null;
  created_by?: string | null;
  assigned_to?: string | null;
}): Promise<StrategicGoal> {
  return request<StrategicGoal>("/projects/goals", { method: "POST", body: payload });
}

export function fetchGoalTasks(goalId: string): Promise<Task[]> {
  return request<Task[]>(`/projects/goals/${encodeURIComponent(goalId)}/tasks`);
}

export function fetchTasks(projectId: string): Promise<Task[]> {
  return request<Task[]>(`/projects/projects/${encodeURIComponent(projectId)}/tasks`);
}

export function createProject(
  goalId: string,
  payload: {
    title: string;
    description?: string | null;
    status?: "backlog" | "in_progress" | "review" | "done";
    due_date?: string | null;
    assignee?: string | null;
    agent_generated?: boolean;
  },
): Promise<Project> {
  return request<Project>(`/projects/${encodeURIComponent(goalId)}/projects`, {
    method: "POST",
    body: payload,
  });
}

export function createGoalTask(
  goalId: string,
  payload: {
    title: string;
    detail?: string | null;
    status?: "todo" | "doing" | "done";
    priority?: "low" | "medium" | "high";
    source_agent?: string | null;
  },
): Promise<Task> {
  return request<Task>(`/projects/goals/${encodeURIComponent(goalId)}/tasks`, {
    method: "POST",
    body: payload,
  });
}

export function createTask(
  projectId: string,
  payload: {
    title: string;
    detail?: string | null;
    status?: "todo" | "doing" | "done";
    priority?: "low" | "medium" | "high";
    source_agent?: string | null;
    due_date?: string | null;
    assigned_to?: string | null;
    created_by?: string | null;
  },
): Promise<Task> {
  return request<Task>(`/projects/projects/${encodeURIComponent(projectId)}/tasks`, {
    method: "POST",
    body: payload,
  });
}

export function updateTask(
  taskId: string,
  payload: Partial<Pick<Task, "title" | "detail" | "status" | "source_agent">>,
): Promise<Task> {
  return request<Task>(`/projects/tasks/${encodeURIComponent(taskId)}`, {
    method: "PATCH",
    body: payload,
  });
}

export function reorderTask(
  taskId: string,
  payload: { status: Task["status"]; position: number },
): Promise<Task> {
  return request<Task>(`/projects/tasks/${encodeURIComponent(taskId)}/reorder`, {
    method: "PATCH",
    body: payload,
  });
}

export function addTaskMemory(taskId: string, summary: string): Promise<TaskMemoryRecord> {
  return request<TaskMemoryRecord>(`/projects/tasks/${encodeURIComponent(taskId)}/memory`, {
    method: "POST",
    body: { summary },
  });
}

export function fetchTaskMemory(taskId: string): Promise<TaskMemoryResponse> {
  return request<TaskMemoryResponse>(`/projects/tasks/${encodeURIComponent(taskId)}/memory`);
}

export function requestTaskReview(taskId: string, payload: { prompt: string; agent: string }): Promise<Task> {
  return request<Task>(`/hitl/tasks/${encodeURIComponent(taskId)}/request_review`, {
    method: "POST",
    body: payload,
  });
}

export function submitTaskReview(
  taskId: string,
  payload: { reviewer: string; response: string; approve: boolean },
): Promise<Task> {
  return request<Task>(`/hitl/tasks/${encodeURIComponent(taskId)}/submit_review`, {
    method: "POST",
    body: payload,
  });
}
